import { Injectable } from '@angular/core';
import { FeatureStatus } from '../population-menu/population.service';
import { MapHttpService } from '../../../../mapbox/services/map-http.service';
import { JsonResponse } from '../../../../../shared/api/backend-config';
import { catchError, of, switchMap } from 'rxjs';
import { RentRange } from '../../../../../shared/model/range';
import {
  getPercents,
  setAccessibleYears,
  sortKeys,
  stillOnYear,
  withLoading,
} from '../shared/chart-utility';
import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import { Data, Layout } from 'plotly.js-dist-min';

export interface HealthInsuranceJSON {
  distribution: HealthInsurance[];
}

export interface HealthInsurance {
  sex: string;
  ageFrom: number;
  ageTo: number;
  healthInsurance: boolean;
  population: number;
}

export interface HealthFeatureStatus extends FeatureStatus {
  containsInsuranceDistribution: boolean;
  insuranceDistributionYear: number;
  containsAirQuality: boolean;
}

export interface AirPollutionDistribution {
  FROM_0_TO_5: number;
  FROM_5_TO_7: number;
  FROM_7_TO_9: number;
  FROM_9_TO_12: number;
  FROM_12_TO_16: number;
  FROM_16_TO_20: number;
  FROM_20: number;
}

export interface AirQuality {
  AIR_POLLUTION_DISTRIBUTION: {
    distribution: AirPollutionDistribution;
  };
}

export const AIR_POLLUTION_DISTRIBUTION = 'AIR_POLLUTION_DISTRIBUTION';
export const INSURANCE_FRACTION_BY_YEARS = 'INSURANCE_FRACTION_BY_YEARS';

export interface InsuranceFractionByYears {
  INSURANCE_FRACTION_BY_YEARS: Record<string, number>;
}

@Injectable({
  providedIn: 'root',
})
export class HealthService {
  // Filled from INSURANCE_FRACTION_BY_YEARS, which spans the same years as the
  // insurance distribution itself.
  public readonly accessibleYears: number[] = [];

  private static readonly insuranceAgeRanges: RentRange[] = [
    new RentRange(0, 18),
    new RentRange(18, 25),
    new RentRange(25, 35),
    new RentRange(35, 45),
    new RentRange(45, 65),
    new RentRange(65, RentRange.MAX_RENT),
  ];

  public readonly featureStatus: HealthFeatureStatus = {
    featureId: undefined,
    isSelected: false,
    containsInsuranceDistribution: false,
    containsAirQuality: false,
    insuranceDistributionYear: 0,
  };

  public readonly insuranceDistributionChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  public airQualityChart: {
    data: Data;
    layout: Layout;
  } = {
    data: [] as Data,
    layout: {} as Layout,
  };

  constructor(private http: MapHttpService) {}

  public fillChartsData(feature: MapboxGeoJSONFeature): void {
    const naturalId = feature.properties!.external_id;

    this.fillAirQualityChart(naturalId);
    this.fillInsuranceChart(naturalId);
  }

  // The distribution endpoint serves one year per request and cannot report which
  // years it holds, so the year list is taken from the by-years fraction first.
  private fillInsuranceChart(naturalId: string): void {
    this.http
      .getDataForSingleCellUnified<InsuranceFractionByYears>(naturalId, [
        INSURANCE_FRACTION_BY_YEARS,
      ])
      .pipe(
        switchMap((res) => {
          const byYear = res.object?.INSURANCE_FRACTION_BY_YEARS ?? {};

          setAccessibleYears(
            this.accessibleYears,
            Object.keys(byYear).map((year) => ({ year: Number(year) }))
          );

          if (!this.accessibleYears.length) {
            return of(null);
          }

          this.featureStatus.insuranceDistributionYear = Math.max(
            ...this.accessibleYears
          );

          return this.http.getInsuranceData(
            naturalId,
            this.featureStatus.insuranceDistributionYear
          );
        }),
        catchError((err) => {
          console.error('ERROR FETCHING INSURANCE DISTRIBUTION', err);
          return of(null);
        }),
        withLoading(this.featureStatus)
      )
      .subscribe((data: JsonResponse<HealthInsuranceJSON> | null) => {
        this.featureStatus.isSelected = true;
        this.featureStatus.naturalId = naturalId;

        if (!data?.object) {
          this.featureStatus.containsInsuranceDistribution = false;
          return;
        }

        this.featureStatus.containsInsuranceDistribution = true;
        this.fillHealthInsuranceChart(data.object.distribution);
      });
  }

  // The distribution endpoint serves one year per request, so switching years
  // only needs that one request - unlike fillInsuranceChart, it must not
  // touch accessibleYears or re-pin insuranceDistributionYear to the max, or
  // the year the visitor just picked gets overwritten once this resolves.
  public updateInsuranceDistributionByYear(year: number): void {
    if (!this.featureStatus.naturalId) {
      return;
    }

    this.featureStatus.insuranceDistributionYear = year;

    this.http
      .getInsuranceData(this.featureStatus.naturalId, year)
      .pipe(
        catchError((err) => {
          console.error('ERROR FETCHING INSURANCE DISTRIBUTION', err);
          return of(null);
        }),
        withLoading(this.featureStatus),
        // Nothing cancels the request for the year before this one, and
        // `fillInsuranceChart` re-pins the year to the newest on arrival, so a
        // response is only worth drawing while its year is still the one shown.
        stillOnYear(() => this.featureStatus.insuranceDistributionYear === year)
      )
      .subscribe((data: JsonResponse<HealthInsuranceJSON> | null) => {
        if (!data?.object) {
          this.featureStatus.containsInsuranceDistribution = false;
          return;
        }

        this.featureStatus.containsInsuranceDistribution = true;
        this.fillHealthInsuranceChart(data.object.distribution);
      });
  }

  private fillHealthInsuranceChart(data: HealthInsurance[]): void {
    const aggregatedData = this.aggregateInsuranceData(
      data,
      HealthService.insuranceAgeRanges
    );

    const keys = sortKeys(Object.keys(aggregatedData));

    const traces: Data = [] as Data;

    ['Insured', 'Uninsured'].forEach((status) => {
      const color = status === 'Insured' ? '#774FA0' : '#EFB743';

      // @ts-ignore
      const malePop = keys.map((key) => aggregatedData[key][status]['Male']);
      // @ts-ignore
      const femalePop = keys.map(
        (key) => (aggregatedData as any)[key][status]['Female']
      );
      const totalPop = malePop.map((pop, i) => pop + femalePop[i]);

      const hoverTexts = totalPop.map(
        (total, i) =>
          `% of ${status}: ${((total / totalPop.reduce((acc, curr) => acc + curr, 0)) * 100).toFixed(1)}%<br>Male: ${getPercents(total, malePop)[i].toFixed(1)}%<br>Female: ${getPercents(total, femalePop)[i].toFixed(1)}%<br>`
      );

      const totalValues = totalPop.reduce((acc, curr) => acc + curr, 0);

      if (totalValues < 10) {
        this.featureStatus.containsInsuranceDistribution = false;
        return;
      }

      (traces as Array<any>).push({
        x: keys,
        y: getPercents(
          totalPop.reduce((acc, curr) => acc + curr, 0),
          totalPop
        ),
        name: status,
        text: hoverTexts,
        textposition: 'none',
        hoverinfo: 'text',
        legendgroup: status,
        marker: { color },
        type: 'bar',
      });
    });

    this.insuranceDistributionChart.data = traces;
    this.insuranceDistributionChart.layout = {
      barmode: 'group',
      autosize: true,
      hovermode: 'closest',
      xaxis: { title: 'Age' },
      yaxis: {
        title: 'People',
        tickformat: ',.0f',
        ticksuffix: '%',
      },
      bargap: 0.3,
      bargroupgap: 0.1,
      legend: {
        x: -0.15,
        y: 1.2,
        orientation: 'h',
      },
      height: 350,
      width: 390,
      margin: { b: 35, r: 30, t: 35 },
      font: {
        family: 'Manrope, sans-serif',
      },
    } as Layout;
  }

  private aggregateInsuranceData(
    data: HealthInsurance[],
    costRangeAggregation: RentRange[]
  ): Record<string, number> {
    let insuranceData: any;

    insuranceData = data
      .map((el) => {
        if (el.ageFrom === el.ageTo) return [];

        let ageFrom = el.ageFrom;

        let ageTo = !!el.ageTo ? el.ageTo : RentRange.MAX_RENT;

        const householdRentRange = new RentRange(ageFrom, ageTo);

        const matchingRanges = costRangeAggregation.filter((range) =>
          range.containsWholeRange(householdRentRange)
        );

        return matchingRanges.map((range) => ({
          sex: el.sex,
          healthInsurance: el.healthInsurance,
          range: range,
          population: el.population,
        }));
      })
      .flat();

    insuranceData!.sort((a: any, b: any) => {
      return a.range.fromInclusive - b.range.fromInclusive;
    });

    const aggregatedData = insuranceData.reduce(
      (acc: any, curr: any) => {
        const rangeStr = curr.range.toInclusiveString();

        // initialize if not exist
        if (!acc[rangeStr]) {
          acc[rangeStr] = {
            Insured: {
              Male: 0,
              Female: 0,
            },
            Uninsured: {
              Male: 0,
              Female: 0,
            },
          };
        }

        // add to the correct category
        const insuranceStatus = curr.healthInsurance ? 'Insured' : 'Uninsured';
        const sex = curr.sex === 'M' ? 'Male' : 'Female';
        acc[rangeStr][insuranceStatus][sex] += curr.population;

        return acc;
      },
      {} as Record<
        string,
        {
          Insured: { Male: number; Female: number };
          Uninsured: { Male: number; Female: number };
        }
      >
    );

    return aggregatedData;
  }

  public fillAirQualityChart(id: string): void {
    this.http
      .getDataForSingleCellUnified<AirQuality>(id, [AIR_POLLUTION_DISTRIBUTION])
      .pipe(withLoading(this.featureStatus))
      .subscribe((res) => {
        if (res.object.AIR_POLLUTION_DISTRIBUTION) {
          this.featureStatus.naturalId = id;
          this.featureStatus.isSelected = true;
          this.featureStatus.containsAirQuality = true;
        }

        const data = res.object.AIR_POLLUTION_DISTRIBUTION.distribution;

        if (!data) {
          this.featureStatus.containsAirQuality = false;
          return;
        }

        const labelMap: Record<keyof AirPollutionDistribution, string> = {
          FROM_0_TO_5: '0–5',
          FROM_5_TO_7: '5–7',
          FROM_7_TO_9: '7–9',
          FROM_9_TO_12: '9–12',
          FROM_12_TO_16: '12–16',
          FROM_16_TO_20: '16–20',
          FROM_20: '20+',
        };

        const categories = Object.keys(
          labelMap
        ) as (keyof AirPollutionDistribution)[];

        const filtered = categories
          .filter((key) => data[key] && data[key] > 0)
          .map((key) => ({
            rawKey: key,
            label: labelMap[key],
            value: data[key],
          }));

        this.airQualityChart.data = [
          {
            x: filtered.map((el) => el.label),
            y: filtered.map((el) => el.value ?? 0),
            type: 'bar',
            orientation: 'v',
            text: filtered.map((el) => `${el.label}: ${el.value} days`),
            textposition: 'none',
            hoverinfo: 'text',
            marker: {
              color: filtered.map((el) => this.getAirQualityColor(el.rawKey)),
            },
          },
        ] as Data;

        this.airQualityChart.layout = {
          autosize: true,
          yaxis: {
            title: {
              text: 'Number of Days',
              font: { size: 14 },
            } as any,
            rangemode: 'tozero',
          },
          xaxis: {
            title: 'PM 2.5 Levels',
            tickangle: -25,
            tickfont: {
              size: 10,
            },
          },
          margin: { t: 35, b: 190, r: 30, pad: 5 },
          bargap: 0.5,
          font: {
            family: 'Manrope, sans-serif',
          },
        } as Layout;
      });
  }

  private getAirQualityColor(category: string): string {
    switch (category) {
      case 'FROM_0_TO_5':
        return '#7BB3D3';
      case 'FROM_5_TO_7':
        return '#9AD64D';
      case 'FROM_7_TO_9':
        return '#F9CE37';
      case 'FROM_9_TO_12':
        return '#F88F4A';
      case 'FROM_12_TO_16':
        return '#D65C5D';
      case 'FROM_16_TO_20':
        return '#9F6FB5';
      case 'FROM_20':
        return '#9A556B';
      default:
        return '#CCCCCC';
    }
  }
}
